import BaseWidget, { escapeHtml } from './baseWidget.js';
import stammdatenService from '../../services/stammdatenService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { getAktuellerZeitraum } from '../../util.js';

/**
 * Zeigt alle Mitarbeiter mit offenen Aufgaben als aufklappbare Liste (Uebersicht.Nachrichten,
 * hier nur die unerledigten). Über die Suche lässt sich zusätzlich ein beliebiger Mitarbeiter
 * (auch ohne offene Aufgaben) in dieselbe Liste aufnehmen, um ihm eine neue Aufgabe zuzuweisen -
 * es gibt also nur eine Liste, keine separate Auswahl-Ansicht daneben.
 */
export default class EmployeeTodoWidget extends BaseWidget {
    constructor(type, instanceId, state = {}) {
        super(type, instanceId, state);
        // Manuell über die Suche hinzugefügte Mitarbeiter, die (noch) keine offene Aufgabe haben
        // und daher sonst nicht in der Liste auftauchen würden.
        this.pinnedMaIds = Array.isArray(state.pinnedMaIds) ? [...state.pinnedMaIds] : [];
        this._maListPromise = null;
        this._documentClickHandler = null;
        this._eintraege = [];
        // Mitarbeiter deren Zeile gerade aufgeklappt ist - mehrere gleichzeitig möglich,
        // öffnen einer Zeile schließt die anderen nicht.
        this._openMaIds = new Set();
    }

    serializeState() {
        return {
            pinnedMaIds: this.pinnedMaIds,
        };
    }

    destroy() {
        if (this._documentClickHandler) {
            document.removeEventListener('click', this._documentClickHandler);
            this._documentClickHandler = null;
        }
    }

    async onRender() {
        this.bodyEl.innerHTML = `
            <div class="widget-ma-search">
                <input type="text" class="ma-search-input" placeholder="Mitarbeiter suchen..." />
                <div class="widget-ma-search-results hidden"></div>
            </div>
            <div class="widget-ma-task-list"></div>
        `;
        const searchInput = this.bodyEl.querySelector('.ma-search-input');
        const resultsDiv = this.bodyEl.querySelector('.widget-ma-search-results');

        // Aktuelle Trefferliste + Index des per Tastatur markierten Eintrags,
        // damit ArrowUp/ArrowDown/Enter im Suchfeld funktionieren.
        let currentMatches = [];
        let highlightedIndex = -1;

        const setHighlight = (idx) => {
            const rows = resultsDiv.querySelectorAll('div[data-id]');
            if (rows.length === 0) {
                highlightedIndex = -1;
                return;
            }
            highlightedIndex = ((idx % rows.length) + rows.length) % rows.length; // wrap & negative-safe
            rows.forEach((row, i) => row.classList.toggle('ma-result-active', i === highlightedIndex));
            const activeRow = rows[highlightedIndex];
            if (activeRow) activeRow.scrollIntoView({ block: 'nearest' });
        };

        const pickMa = async (ma) => {
            if (!ma) return;
            searchInput.value = '';
            resultsDiv.classList.add('hidden');
            currentMatches = [];
            highlightedIndex = -1;
            await this.openOrAddMa(ma._id);
        };

        searchInput.addEventListener('input', async () => {
            const term = searchInput.value.trim().toLowerCase();
            if (!term) {
                resultsDiv.classList.add('hidden');
                currentMatches = [];
                highlightedIndex = -1;
                return;
            }
            const malist = await this.getMaList();
            currentMatches = malist
                .filter((ma) => `${ma.Vorname} ${ma.Nachname}`.toLowerCase().includes(term)
                    || `${ma.Nachname} ${ma.Vorname}`.toLowerCase().includes(term))
                .sort((a, b) => {
                    // Gleiche Namen: aktuellste Beschäftigung (späteres Eintrittsdatum) zuerst
                    const nameCmp = `${a.Nachname}, ${a.Vorname}`.localeCompare(`${b.Nachname}, ${b.Vorname}`, 'de');
                    if (nameCmp !== 0) return nameCmp;
                    return (b.Eintrittsdatum || '').localeCompare(a.Eintrittsdatum || '');
                })
                .slice(0, 10); // max 10 Treffer anzeigen
            if (currentMatches.length === 0) {
                resultsDiv.innerHTML = '<div>Keine Treffer</div>';
            } else {
                resultsDiv.innerHTML = currentMatches
                    .map((ma) => `
                        <div data-id="${escapeHtml(ma._id)}">
                            <div class="ma-result-name">${escapeHtml(`${ma.Nachname}, ${ma.Vorname}`)}</div>
                            <div class="ma-result-meta">${escapeHtml(formatBeschaeftigungInfo(ma.Personalnummer, ma.Eintrittsdatum, ma.Austrittsdatum))}</div>
                        </div>
                    `)
                    .join('');
            }
            resultsDiv.classList.remove('hidden');
            // Ersten Treffer als Vorauswahl markieren, damit Enter direkt funktioniert.
            highlightedIndex = currentMatches.length > 0 ? 0 : -1;
            resultsDiv.querySelectorAll('div[data-id]').forEach((d, i) => {
                d.classList.toggle('ma-result-active', i === highlightedIndex);
                d.addEventListener('click', () => pickMa(currentMatches.find((m) => m._id === d.dataset.id)));
                d.addEventListener('mouseenter', () => setHighlight(i));
            });
        });

        searchInput.addEventListener('keydown', (e) => {
            if (resultsDiv.classList.contains('hidden') || currentMatches.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight(highlightedIndex + 1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight(highlightedIndex - 1);
            } else if (e.key === 'Enter') {
                if (highlightedIndex >= 0 && highlightedIndex < currentMatches.length) {
                    e.preventDefault();
                    pickMa(currentMatches[highlightedIndex]);
                }
            } else if (e.key === 'Escape') {
                resultsDiv.classList.add('hidden');
            }
        });

        // Klick außerhalb schließt die Trefferliste
        if (this._documentClickHandler) {
            document.removeEventListener('click', this._documentClickHandler);
        }
        this._documentClickHandler = (e) => {
            if (!this.bodyEl.contains(e.target)) {
                resultsDiv.classList.add('hidden');
            }
        };
        document.addEventListener('click', this._documentClickHandler);

        await this.loadListe();
    }

    async getMaList() {
        if (!this._maListPromise) {
            const zeitraum = getAktuellerZeitraum();
            this._maListPromise = stammdatenService
                .holeMitarbeiterListe(stammdatenService.aktuelleBetriebsstaette, zeitraum)
                .then((list) => list.filter((ma) => !ma.Archiviert));
        }
        return this._maListPromise;
    }

    /**
     * Wird über die Suche aufgerufen: Ist der Mitarbeiter schon in der Liste (weil er offene
     * Aufgaben hat oder bereits angepinnt ist), wird nur seine Zeile aufgeklappt. Andernfalls
     * wird er (ohne offene Aufgaben) neu in die Liste aufgenommen und aufgeklappt.
     * @param {string} maId
     */
    async openOrAddMa(maId) {
        const bereitsInListe = this._eintraege.some((eintrag) => eintrag.id === maId);
        if (!bereitsInListe && !this.pinnedMaIds.includes(maId)) {
            this.pinnedMaIds.push(maId);
            if (window.myServices?.dashboard) {
                window.myServices.dashboard.saveConfig();
            }
        }
        await this.loadListe(maId);
        const row = this.bodyEl.querySelector(`.widget-ma-task-row[data-ma-id="${maId}"]`);
        if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    /**
     * Lädt alle Mitarbeiter mit offenen Aufgaben plus alle manuell angepinnten Mitarbeiter
     * und rendert sie akkordeonartig.
     * @param {string} [openMaId] Mitarbeiter, dessen Zeile direkt aufgeklappt werden soll
     */
    async loadListe(openMaId) {
        const listEl = this.bodyEl.querySelector('.widget-ma-task-list');
        listEl.innerHTML = '<div class="widget-empty">Lade...</div>';
        if (openMaId !== undefined) this._openMaIds.add(openMaId);

        const malist = await this.getMaList();
        // Uebersicht.NeueNachricht === Mitarbeiter hat mind. eine offene Aufgabe in Uebersicht.Nachrichten
        const maIds = new Set(malist.filter((ma) => ma.NeueNachricht).map((ma) => ma._id));
        this.pinnedMaIds.forEach((id) => maIds.add(id));

        const eintraege = (await Promise.all(Array.from(maIds).map((id) => this.buildEintrag(id)))).filter(Boolean);
        eintraege.sort((a, b) => this.offeneAnzahl(b) - this.offeneAnzahl(a) || a.name.localeCompare(b.name, 'de'));
        this._eintraege = eintraege;
        this.renderListe();
    }

    async buildEintrag(maId) {
        const maObj = await stammdatenService.holeMitarbeiter(maId);
        if (!maObj || !maObj._id) return null;
        const aufgaben = (maObj.Uebersicht?.Nachrichten || []).filter((a) => this.istSichtbareAufgabe(a));
        const beschaef = maObj.Beschaeftigung?.[0] || {};
        return {
            id: maObj._id,
            name: `${maObj.Personalien.Nachname}, ${maObj.Personalien.Vorname}`,
            initialen: getInitialen(maObj.Personalien.Vorname, maObj.Personalien.Nachname),
            meta: formatBeschaeftigungInfo(maObj.Personalien.Personalnummer, beschaef.Eintrittsdatum, beschaef.Austrittsdatum),
            aufgaben,
        };
    }

    /**
     * Offene Aufgaben werden immer angezeigt, erledigte bleiben zusätzlich noch einen Monat
     * sichtbar (als erledigt markiert), damit sie nicht sofort aus der Liste verschwinden.
     */
    istSichtbareAufgabe(aufgabe) {
        if (!aufgabe.Erledigt) return true;
        return !!aufgabe.BearbeitetDatum && dayjs(aufgabe.BearbeitetDatum).isAfter(dayjs().subtract(1, 'month'));
    }

    offeneAnzahl(eintrag) {
        return eintrag.aufgaben.filter((a) => !a.Erledigt).length;
    }

    renderListe() {
        const listEl = this.bodyEl.querySelector('.widget-ma-task-list');
        if (!listEl) return;
        if (this._eintraege.length === 0) {
            listEl.innerHTML = '<div class="widget-empty">Keine offenen Aufgaben. Mitarbeiter oben suchen, um eine Aufgabe zuzuweisen.</div>';
            return;
        }
        listEl.innerHTML = '';
        this._eintraege.forEach((eintrag) => listEl.appendChild(this.renderMaRow(eintrag)));
    }

    renderMaRow(eintrag) {
        const isOpen = this._openMaIds.has(eintrag.id);
        const istLeer = eintrag.aufgaben.length === 0;
        const offen = this.offeneAnzahl(eintrag);
        const row = document.createElement('div');
        row.className = `widget-ma-task-row${isOpen ? ' open' : ''}`;
        row.dataset.maId = eintrag.id;
        row.innerHTML = `
            <div class="widget-ma-task-head" role="button" tabindex="0" aria-expanded="${isOpen}">
                <div class="widget-ma-task-avatar">${escapeHtml(eintrag.initialen)}</div>
                <div class="widget-ma-task-info">
                    <div class="widget-ma-task-name">${escapeHtml(eintrag.name)}</div>
                    <div class="widget-ma-task-meta">${escapeHtml(eintrag.meta)}</div>
                </div>
                <div class="widget-ma-task-count${offen === 0 ? ' zero' : ''}">${offen}</div>
                ${istLeer ? '<i class="bi bi-x widget-ma-task-remove" title="Aus Liste entfernen"></i>' : ''}
                <i class="bi bi-chevron-down widget-ma-task-chevron"></i>
            </div>
            <div class="widget-ma-task-body"></div>
        `;
        const body = row.querySelector('.widget-ma-task-body');
        if (istLeer) {
            const empty = document.createElement('div');
            empty.className = 'widget-ma-task-empty';
            empty.textContent = 'Keine offenen Aufgaben.';
            body.appendChild(empty);
        } else {
            eintrag.aufgaben
                .slice()
                .sort((a, b) => new Date(b.ErstelltDatum) - new Date(a.ErstelltDatum))
                .forEach((aufgabe) => body.appendChild(this.renderItem(aufgabe, eintrag.id)));
        }
        body.appendChild(this.renderAddRow(eintrag.id));

        const head = row.querySelector('.widget-ma-task-head');
        const toggle = () => {
            if (this._openMaIds.has(eintrag.id)) this._openMaIds.delete(eintrag.id);
            else this._openMaIds.add(eintrag.id);
            this.renderListe();
        };
        head.addEventListener('click', toggle);
        head.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
            }
        });

        const removeBtn = row.querySelector('.widget-ma-task-remove');
        if (removeBtn) {
            removeBtn.addEventListener('click', (e) => this.entferneAusListe(e, eintrag.id));
        }
        return row;
    }

    async entferneAusListe(e, maId) {
        e.stopPropagation();
        this.pinnedMaIds = this.pinnedMaIds.filter((id) => id !== maId);
        this._openMaIds.delete(maId);
        if (window.myServices?.dashboard) {
            window.myServices.dashboard.saveConfig();
        }
        await this.loadListe();
    }

    renderItem(aufgabe, maId) {
        const item = document.createElement('div');
        item.className = `widget-todo-item${aufgabe.Erledigt ? ' erledigt' : ''}`;
        item.innerHTML = `
            <input type="checkbox" ${aufgabe.Erledigt ? 'checked' : ''} />
            <div class="widget-todo-text">${escapeHtml(aufgabe.Nachricht)}</div>
            <span class="widget-todo-meta" title="${escapeHtml(this.formatAufgabenDatumLang(aufgabe))}">${escapeHtml(this.formatAufgabenDatumKurz(aufgabe))}</span>
        `;
        // Checkbox bleibt in beide Richtungen bedienbar, damit ein versehentliches Abhaken
        // wieder rückgängig gemacht werden kann.
        item.querySelector('input').addEventListener('change', async (e) => {
            const erledigt = e.target.checked;
            const aktualisiert = await stammdatenService.updateMitarbeiterAufgabe(maId, aufgabe._id, aufgabe.Nachricht, erledigt);
            if (aktualisiert) {
                aufgabe = aktualisiert;
                this.aktualisiereAufgabe(maId, item, aktualisiert);
            } else {
                e.target.checked = !erledigt; // Speichern fehlgeschlagen: Checkbox-Zustand zurücksetzen
            }
        });
        return item;
    }

    // Kein Fälligkeitsdatum im Datenmodell vorhanden: offene Aufgaben zeigen ihr Erstelldatum,
    // erledigte (die noch bis zu einen Monat sichtbar bleiben) ihr Erledigt-Datum. Kurzform für
    // die Anzeige (Datum + Initialen), Langform (wie in ma_uebersicht.js) als Tooltip.
    formatAufgabenDatumKurz(aufgabe) {
        if (aufgabe.Erledigt) {
            if (!aufgabe.BearbeitetDatum) return '';
            const initialen = getInitialenAusName(aufgabe.BearbeitetVon);
            return `erledigt ${dayjs(aufgabe.BearbeitetDatum).format('DD.MM.')}${initialen ? `, ${initialen}` : ''}`;
        }
        if (!aufgabe.ErstelltDatum) return '';
        const initialen = getInitialenAusName(aufgabe.ErstelltVon);
        return `erstellt ${dayjs(aufgabe.ErstelltDatum).format('DD.MM.')}${initialen ? `, ${initialen}` : ''}`;
    }

    formatAufgabenDatumLang(aufgabe) {
        if (aufgabe.Erledigt) {
            if (!aufgabe.BearbeitetDatum) return '';
            const von = aufgabe.BearbeitetVon ? ` von ${aufgabe.BearbeitetVon}` : '';
            return `erledigt am ${dayjs(aufgabe.BearbeitetDatum).format('DD.MM.YYYY')}${von}`;
        }
        if (!aufgabe.ErstelltDatum) return '';
        const von = aufgabe.ErstelltVon ? ` von ${aufgabe.ErstelltVon}` : '';
        return `erstellt am ${dayjs(aufgabe.ErstelltDatum).format('DD.MM.YYYY')}${von}`;
    }

    /**
     * Aktualisiert nur die betroffene Aufgabenzeile + den Zähler der Mitarbeiterzeile in-place,
     * statt die komplette Liste neu vom Server zu laden.
     */
    aktualisiereAufgabe(maId, itemEl, aktualisiert) {
        const eintrag = this._eintraege.find((e) => e.id === maId);
        if (eintrag) {
            const idx = eintrag.aufgaben.findIndex((a) => a._id === aktualisiert._id);
            if (idx >= 0) eintrag.aufgaben[idx] = aktualisiert;
        }
        this._maListPromise = null; // Uebersicht.NeueNachricht hat sich serverseitig geändert (nächster voller Load)

        itemEl.classList.toggle('erledigt', aktualisiert.Erledigt);
        itemEl.querySelector('input').checked = aktualisiert.Erledigt;
        const metaEl = itemEl.querySelector('.widget-todo-meta');
        metaEl.textContent = this.formatAufgabenDatumKurz(aktualisiert);
        metaEl.title = this.formatAufgabenDatumLang(aktualisiert);

        if (eintrag) this.aktualisiereZeilenkopf(maId, eintrag);
    }

    /**
     * Aktualisiert Zähler-Badge und "Entfernen"-Icon einer bereits gerenderten Zeile,
     * ohne die Zeile selbst neu aufzubauen.
     */
    aktualisiereZeilenkopf(maId, eintrag) {
        const row = this.bodyEl.querySelector(`.widget-ma-task-row[data-ma-id="${maId}"]`);
        if (!row) return;
        const offen = this.offeneAnzahl(eintrag);
        const badge = row.querySelector('.widget-ma-task-count');
        if (badge) {
            badge.textContent = offen;
            badge.classList.toggle('zero', offen === 0);
        }
        const istLeer = eintrag.aufgaben.length === 0;
        const removeBtn = row.querySelector('.widget-ma-task-remove');
        if (istLeer && !removeBtn) {
            const icon = document.createElement('i');
            icon.className = 'bi bi-x widget-ma-task-remove';
            icon.title = 'Aus Liste entfernen';
            icon.addEventListener('click', (e) => this.entferneAusListe(e, maId));
            row.querySelector('.widget-ma-task-chevron').before(icon);
        } else if (!istLeer && removeBtn) {
            removeBtn.remove();
        }
    }

    renderAddRow(maId) {
        const wrap = document.createElement('div');
        wrap.className = 'widget-add-row widget-ma-task-add';
        wrap.innerHTML = `
            <textarea class="todo-input" rows="2" placeholder="Neue Aufgabe..."></textarea>
            <button type="button" class="todo-add" title="Hinzufügen"><i class="bi bi-plus-lg"></i></button>
        `;
        const input = wrap.querySelector('.todo-input');
        const addFn = async () => {
            const value = input.value.trim();
            if (!value) return;
            const neueAufgabe = await stammdatenService.postMitarbeiterAufgabe(maId, value);
            if (neueAufgabe) {
                input.value = '';
                this._maListPromise = null; // Uebersicht.NeueNachricht hat sich serverseitig geändert (nächster voller Load)
                this.fuegeAufgabeEin(maId, neueAufgabe, wrap);
            }
        };
        wrap.querySelector('.todo-add').addEventListener('click', addFn);
        // Enter sendet ab; Shift+Enter erlaubt Mehrzeiligkeit.
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                addFn();
            }
        });
        return wrap;
    }

    /**
     * Fügt eine neu erstellte Aufgabe direkt in die schon gerenderte Zeile ein,
     * statt die komplette Liste neu vom Server zu laden.
     */
    fuegeAufgabeEin(maId, aufgabe, addRowEl) {
        const eintrag = this._eintraege.find((e) => e.id === maId);
        // Zeile wurde zwischenzeitlich entfernt (z.B. durch parallele Suche/Neuladen) - dann
        // reicht ein voller Reload statt eines fehleranfälligen Inline-Einfügens.
        if (!eintrag) {
            this.loadListe(maId);
            return;
        }
        eintrag.aufgaben.unshift(aufgabe);

        const body = addRowEl.closest('.widget-ma-task-body');
        const empty = body.querySelector('.widget-ma-task-empty');
        if (empty) empty.remove();
        body.insertBefore(this.renderItem(aufgabe, maId), body.querySelector('.widget-todo-item') || addRowEl);
        this.aktualisiereZeilenkopf(maId, eintrag);
    }
}

function formatBeschaeftigungInfo(personalnummer, eintritt, austritt) {
    const parts = [];
    if (personalnummer) parts.push(`Pers.Nr. ${personalnummer}`);
    const eintrittDay = eintritt ? dayjs(eintritt) : null;
    const austrittDay = austritt ? dayjs(austritt) : null;
    if (eintrittDay?.isValid() && austrittDay?.isValid()) {
        parts.push(`${eintrittDay.format('DD.MM.YYYY')} – ${austrittDay.format('DD.MM.YYYY')}`);
    } else if (eintrittDay?.isValid()) {
        const heute = dayjs().startOf('day');
        const prefix = eintrittDay.isAfter(heute) ? 'ab' : 'seit';
        parts.push(`${prefix} ${eintrittDay.format('DD.MM.YYYY')}`);
    }
    return parts.join(' · ');
}

function getInitialen(vorname, nachname) {
    return `${(vorname || '').charAt(0)}${(nachname || '').charAt(0)}`.toUpperCase();
}

// ErstelltVon/BearbeitetVon liegen als ein einzelner "Vorname Nachname"-String vor (nicht
// getrennt wie bei den Mitarbeiter-Stammdaten), daher ein eigener Helper dafür.
function getInitialenAusName(name) {
    const teile = (name || '').trim().split(/\s+/).filter(Boolean);
    if (teile.length === 0) return '';
    return `${teile[0].charAt(0)}${teile[teile.length - 1].charAt(0)}`.toUpperCase();
}
